<template>
  <div class="step-page">
    <div class="step-header">
      <div class="step-badge">Projet : {{ projectName }}</div>
      <h1 class="step-title">Calcul de l'étude</h1>
    </div>

    <!-- État : idle / en attente -->
    <div v-if="calculStatus === 'idle'" class="calcul-panel">
      <div class="calcul-summary">
        <h2 class="calcul-summary__title">Récapitulatif avant calcul</h2>
        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-item__label">Puissance max toiture</div>
            <div class="summary-item__value">{{ nombreFr(puissanceTotaleKwc, 2) }} kWc</div>
          </div>
          <div class="summary-item">
            <div class="summary-item__label">{{ toitures.length > 1 ? 'Orientation par toiture' : 'Orientation' }}</div>
            <div class="summary-item__value" v-if="modeleProduction === 'db'">{{ orientation }} <span class="summary-item__note">(modèle Systèmes Solaires)</span></div>
            <div class="summary-item__value" v-else-if="toitures.length === 1">
              {{ compassLabel(toitures[0].orientationDegres) }} ({{ toitures[0].orientationDegres }}°), inclinaison {{ toitures[0].inclinaisonDeg }}°
            </div>
            <div class="summary-item__value summary-item__value--list" v-else-if="toitures.length > 1">
              <div v-for="(t, idx) in toitures" :key="idx">
                Toiture {{ idx + 1 }} : {{ compassLabel(t.orientationDegres) }} ({{ t.orientationDegres }}°), {{ t.inclinaisonDeg }}°
              </div>
            </div>
            <div class="summary-item__value" v-else>—</div>
          </div>
          <div class="summary-item">
            <div class="summary-item__label">Consommation annuelle</div>
            <div class="summary-item__value">
              {{ formatKwh(consommationAnnuelleKwh) }} kWh<br>
              <span style="font-size:14px; font-weight:600;">≈ {{ formatEur(estimationFactureAnnuelle) }} €/an estimés</span>
            </div>
          </div>
          <div class="summary-item">
            <template v-if="typeTarif === 'unique'">
              <div class="summary-item__label">Tarif unique</div>
              <div class="summary-item__value">{{ nombreFr(tarifHP, 2) }} c€/kWh HT</div>
            </template>
            <template v-else-if="typeTarif === 'saisonnier' || typeTarif === 'hsc_saisonnier'">
              <div class="summary-item__label">{{ typeTarif === 'hsc_saisonnier' ? 'Tarif HP / HC / HSC saisonnier' : 'Tarif d\'achat électricité' }}</div>
              <div class="summary-item__value recap-tarif">
                HP : hiver {{ nombreFr(tarifHPHiver, 2) }} · été {{ nombreFr(tarifHPEte, 2) }} c€/kWh HT<br>
                HC : hiver {{ nombreFr(tarifHCHiver, 2) }} · été {{ nombreFr(tarifHCEte, 2) }} c€/kWh HT
                <template v-if="typeTarif === 'hsc_saisonnier'"><br>HSC : hiver {{ nombreFr(tarifHSCHiver, 2) }} · été {{ nombreFr(tarifHSCEte, 2) }} c€/kWh HT</template>
              </div>
            </template>
            <template v-else-if="typeTarif === 'hsc'">
              <div class="summary-item__label">Tarif HP / HC / HSC</div>
              <div class="summary-item__value recap-tarif">HP {{ nombreFr(tarifHP, 2) }} · HC {{ nombreFr(tarifHC, 2) }} · HSC {{ nombreFr(tarifHSC, 2) }} c€/kWh HT</div>
            </template>
            <template v-else>
              <div class="summary-item__label">Tarif HP / HC</div>
              <div class="summary-item__value">HP {{ nombreFr(tarifHP, 2) }} · HC {{ nombreFr(tarifHC, 2) }} c€/kWh HT</div>
            </template>
          </div>
        </div>

        <div class="calcul-strategy-info">
          <div class="strategy-icon">🔍</div>
          <div>
            <strong>Stratégie d'optimisation ROI</strong>
            <p>
              Le calcul va tester plusieurs configurations de puissance PV,
              avec et sans batterie, et sélectionner automatiquement la combinaison offrant le meilleur retour sur investissement.
            </p>
          </div>
        </div>

        <button class="btn-launch" @click="lancerCalcul">
          🚀 Lancer le calcul
        </button>
      </div>
    </div>

    <!-- État : en cours -->
    <div v-if="calculStatus === 'running'" class="calcul-panel">
      <div class="calcul-progress-wrapper">
        <div class="calcul-spinner"></div>
        <h2 class="calcul-running__title">{{ calculMessage || 'Calcul en cours...' }}</h2>
        <div class="progress-bar-outer">
          <div class="progress-bar-inner" :style="{ width: calculProgress + '%' }"></div>
        </div>
        <div class="progress-label">{{ calculProgress }}%</div>
        <p v-if="miseAJourAuto" class="calcul-info calcul-info--maj">
          Mise à jour de l'étude : les prix de l'électricité, saisis TTC dans l'ancienne version, ont été ramenés en HT
          (RETIX ajoute la TVA pour un particulier). Le rapport est recalculé.
        </p>
        <p class="calcul-info">
          Les scénarios sont calculés en balayant les puissances PV possibles.<br />
          Cette opération peut prendre quelques secondes.
        </p>
      </div>
    </div>

    <!-- État : erreur -->
    <div v-if="calculStatus === 'error'" class="calcul-panel">
      <div class="calcul-error">
        <div class="calcul-error__icon">❌</div>
        <h2>Une erreur est survenue</h2>
        <p>{{ calculMessage }}</p>
        <button class="btn-launch" @click="lancerCalcul">Réessayer</button>
      </div>
    </div>

    <!-- État : terminé → passer au rapport -->
    <div v-if="calculStatus === 'done'" class="calcul-panel">
      <div class="calcul-done">
        <div class="calcul-done__icon">✅</div>
        <h2>Calcul terminé !</h2>
        <p>La configuration optimale a été trouvée. Consultez le rapport ci-dessous.</p>
        <button class="btn-report" @click="$emit('next')">Voir le rapport →</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import axios from 'axios'
import { calculerEtude } from '@/services/calculEtude'
import { authenticationService } from '@/services/authentication'
import { conf } from '@/config/config'
import { nombreFr } from '@/utils/format'

export default {
  name: 'Step5Calcul',
  data() {
    return { miseAJourAuto: false }
  },
  // étude de l'ancienne version rouverte (prix ramenés en HT, schéma v6) : recalcul aussitôt (13/10/2026)
  mounted() {
    if (this.$store.state.wizard.recalculAuto) {
      this.$store.commit('wizard/SET_RECALCUL_AUTO', false)
      this.miseAJourAuto = true
      this.lancerCalcul()
    }
  },
  beforeDestroy() {
    // une réponse arrivée après le départ de l'étape est ignorée : le store est peut-être déjà celui d'une autre étude (22/10/2026)
    this.ecranQuitte = true
  },
  computed: {
    ...mapState('wizard', [
      'projectName', 'puissanceTotaleKwc', 'puissanceModuleWc', 'orientation', 'profileSource', 'profilPredifini',
      'tarifHP', 'tarifHC', 'calculStatus', 'calculProgress', 'calculMessage',
      'csvFile', 'consumptionCurveId', 'draftId',
      'inflationElec', 'capaciteUtilisableBatterie', 'puissanceAbonnement', 'degradationPanneaux', 'degradationBatterie', 'rendementBatterie', 'rendementConversionBatterie',
      'coutInstallationPV', 'coutInstallationBatterie', 'rachatSurplusUnitaire', 'zeroInjection',
      'coutRaccordementEnedis', 'travauxSupplementaires', 'travauxSupplementairesLibelle', 'prixDatalogger', 'avecMaintenance', 'avecAssurance', 'coutMaintenance', 'coutAssurance',
      'longueurCable', 'erp', 'prixCable', 'prixRaccordsAlu', 'raccordement', 'chiffrageMateriel', 'materiel', 'panneauCatalogue',
      'avecFinancement', 'apportPct', 'tauxEmprunt', 'dureeEmprunt', 'dureeEmpruntAuPlusCourt',
      'latitude', 'longitude', 'inclinaisonDeg', 'tarifAbonnementAnnuel', 'taxeelec', 'periodesHC',
      'tarifHSC', 'tarifHSCHiver', 'tarifHSCEte', 'periodesHSC', 'periodesHCEte', 'periodesHCHiver', 'periodesHSCEte', 'periodesHSCHiver',
      'typeTarif', 'tarifHPHiver', 'tarifHCHiver', 'tarifHPEte', 'tarifHCEte',
      'clientLastName', 'clientFirstName', 'clientType', 'address',
      'pvRangeMin', 'pvRangeMax', 'batRangeMin', 'batRangeMax',
      'modeleProduction', 'orientationDegres', 'mountingplace', 'pvProfileId', 'toitures',
      'hausseMaintenancePct', 'hausseMaintenancePeriode', 'inflationAssurance', 'consommationAnnuelleKwh', 'estimationFactureAnnuelle',
    ]),
  },
  methods: {
    nombreFr,
    ...mapMutations('wizard', ['SET_CALCUL', 'SET_RESULT', 'SET_IDS']),

    formatKwh(v) { return v ? Math.round(v).toLocaleString('fr-FR') : '—' },
    formatEur(v) { return v ? Math.round(v).toLocaleString('fr-FR') : '—' },

    compassLabel(deg) {
      var labels = ['Nord', 'Nord-Est', 'Est', 'Sud-Est', 'Sud', 'Sud-Ouest', 'Ouest', 'Nord-Ouest']
      var normalized = ((deg % 360) + 360) % 360
      var idx = Math.round(normalized / 45) % 8
      return labels[idx]
    },

    async saveResults(bothResults) {
      if (!this.draftId) return
      try {
        const token = authenticationService.status.jwtToken
        await axios.post(
          conf.API_URL + '/wizard/draft/' + this.draftId + '/result',
          {
            draftId: this.draftId,
            sansBatterieJson: JSON.stringify(bothResults.sansBatterie || null),
            avecBatterieJson: JSON.stringify(bothResults.avecBatterie || null),
            matrixJson: JSON.stringify(bothResults.matrix || []),
          },
          { headers: { Authorization: 'Bearer ' + token } }
        )
      } catch (e) { /* silencieux */ }
    },

    async completeDraft(customerId) {
      if (!this.draftId) return
      try {
        const token = authenticationService.status.jwtToken
        const wizardState = this.$store.state.wizard
        // État de l'étude sans les résultats : ils sont dans leur table (wizard_draft_result), relus à la réouverture
        // (26/10/2026 : ils étaient aussi copiés ici, plusieurs Mo envoyés deux fois)
        const skip = new Set(['csvFile', 'simulationResult', 'resultSansBatterie', 'resultAvecBatterie', 'matrixResults'])
        const fullState = {}
        Object.keys(wizardState).forEach(k => { if (!skip.has(k)) fullState[k] = wizardState[k] })
        await axios.post(
          conf.API_URL + '/wizard/draft',
          {
            id: this.draftId,
            status: 'COMPLETED',
            customerId,
            stepReached: 6,
            projectName: this.projectName || '',
            wizardJson: JSON.stringify(fullState),
          },
          { headers: { Authorization: 'Bearer ' + token } }
        )
      } catch (e) { /* silencieux */ }
    },

    async lancerCalcul() {
      this.SET_CALCUL({ status: 'running', progress: 5, message: 'Préparation des données...' })

      try {
        // Étape A : upload CSV si nécessaire, puis lancer le calcul d'optimisation
        this.SET_CALCUL({ status: 'running', progress: 15, message: 'Envoi des paramètres...' })

        this.SET_CALCUL({ status: 'running', progress: 30, message: 'Calcul des scénarios en cours...' })
        const bothResults = await calculerEtude(this.$store.state.wizard)
        if (this.ecranQuitte) return
        this.SET_CALCUL({ status: 'running', progress: 90, message: 'Finalisation...' })

        this.SET_RESULT(bothResults)
        // plus de client de l'ancien parcours (26/10/2026) : l'étude est marquée terminée dans tous les cas
        await this.completeDraft(null)
        // résultat déjà enregistré par le serveur : rien à renvoyer
        if (!bothResults.resultatEnregistre) await this.saveResults(bothResults)

        this.SET_CALCUL({ status: 'done', progress: 100, message: 'Calcul terminé' })

        // passage au rapport aussitôt le calcul terminé (13/10/2026 : plus de délai d'1 s, la page changeait sous la souris)
        this.$emit('next')

      } catch (err) {
        if (this.ecranQuitte) return
        let msg = 'Erreur lors du calcul'
        const data = err && err.response ? err.response.data : null
        // UtilsService.error(e) met le message métier dans error.message ; message vaut « Internal server error »
        if (data && data.error && data.error.message) {
          msg = data.error.message
        } else if (data && data.message) {
          msg = data.message
        } else if (err && err.message) {
          msg = err.message
        }
        this.SET_CALCUL({ status: 'error', message: msg })
      }
    },
  },
}
</script>

<style scoped>
.step-page {
  font-family: 'Raleway', sans-serif;
  background: #f5f5f5;
  min-height: calc(100vh - 140px);
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.step-header { display: flex; flex-direction: column; align-items: flex-start; margin-bottom: 4px; }

.step-badge {
  display: inline-block;
  background: #f5c518;
  color: #222;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 12px;
  margin-bottom: 8px;
}

.step-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 4px 0;
}

.breadcrumb { font-size: 13px; color: #888; }
.breadcrumb a { color: #f5a518; text-decoration: none; }
.breadcrumb-sep { margin: 0 8px; }

/* Panneau calcul */
.calcul-panel {
  background: #fff;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: center;
}

.calcul-summary { max-width: 640px; width: 100%; }

.calcul-summary__title {
  font-size: 18px;
  font-weight: 700;
  color: #222;
  margin-bottom: 20px;
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 28px;
}

.summary-item {
  background: #f9f9f9;
  border-radius: 10px;
  padding: 14px 18px;
}

.summary-item__label {
  font-size: 11px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}

.summary-item__value {
  font-size: 18px;
  font-weight: 700;
  color: #222;
}
.summary-item__note { font-size: 13px; font-weight: 500; color: #888; }

.summary-item__value--list {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.6;
}

.calcul-strategy-info {
  display: flex;
  gap: 16px;
  background: #fffde7;
  border: 1px solid #f5c518;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 28px;
}

.strategy-icon { font-size: 24px; flex-shrink: 0; }

.calcul-strategy-info strong { font-size: 14px; color: #222; display: block; margin-bottom: 4px; }
.calcul-strategy-info p { font-size: 13px; color: #666; margin: 0; line-height: 1.5; }

.btn-launch {
  display: block;
  width: 100%;
  padding: 15px;
  border: none;
  border-radius: 24px;
  background: #f5c518;
  color: #222;
  font-size: 16px;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  text-align: center;
}

.btn-launch:hover { background: #e6b800; }

/* Spinner + progress */
.calcul-progress-wrapper {
  text-align: center;
  max-width: 400px;
  width: 100%;
}

.calcul-spinner {
  width: 56px;
  height: 56px;
  border: 5px solid #f0f0f0;
  border-top: 5px solid #f5c518;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 20px auto;
}

@keyframes spin { to { transform: rotate(360deg); } }

.calcul-running__title {
  font-size: 18px;
  font-weight: 700;
  color: #222;
  margin-bottom: 20px;
}

.progress-bar-outer {
  background: #f0f0f0;
  border-radius: 8px;
  height: 12px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-bar-inner {
  height: 100%;
  background: linear-gradient(90deg, #f5c518, #f5a518);
  border-radius: 8px;
  transition: width 0.4s ease;
}

.progress-label {
  font-size: 14px;
  font-weight: 700;
  color: #555;
  margin-bottom: 16px;
}

.calcul-info {
  font-size: 13px;
  color: #aaa;
  line-height: 1.5;
}

/* Done / Error */
.calcul-done, .calcul-error {
  text-align: center;
  max-width: 400px;
}

.calcul-done__icon, .calcul-error__icon { font-size: 48px; margin-bottom: 16px; }

.btn-report {
  padding: 13px 40px;
  border: none;
  border-radius: 24px;
  background: #f5c518;
  color: #222;
  font-size: 15px;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  cursor: pointer;
  margin-top: 16px;
}

.btn-report:hover { background: #e6b800; }

.calcul-info--maj { color: #b35c00; font-weight: 600; }
</style>
